/**
 * Anchored filter popover for a table column header.
 *
 * Parent renders a `<button className="thFilter">…</button>` and a
 * `<ColumnFilterPopover open anchor onClose>` as siblings inside the same `<th>`.
 * The popover positions itself absolutely below-right of the anchor, and
 * closes on outside click or Escape.
 *
 * Content is a slot — parent fills it with whatever filter UI the column needs
 * (text input, checkbox list, date range, etc.). Common patterns:
 *   - MultiSelect of labels → list of `.colFilterCheckbox` rows
 *   - Text contains → a single `<input className="input">`
 *   - Date range → two date inputs plus a checkbox
 *
 * Wrap the UI in a parent that mounts the popover conditionally
 * (`{open && <ColumnFilterPopover>…</ColumnFilterPopover>}`).
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";

export function ColumnFilterPopover(props: {
  anchor: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional title rendered at the top of the popover. */
  title?: string;
  /** Called when the user clicks "Clear" in the popover footer. Omitted = no Clear button. */
  onClear?: () => void;
  /** Suppress the built-in footer row (Clear/Done). Caller provides own actions inside children. */
  hideActions?: boolean;
  /** Override the popover's minWidth in px. Default 220. */
  minWidth?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Flip above the anchor when there isn't enough room below. Measured after
  // first render so the decision accounts for the popover's actual height.
  const [flipUp, setFlipUp] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    const anchorRect = props.anchor?.getBoundingClientRect();
    if (!el || !anchorRect) return;
    const popoverHeight = el.offsetHeight;
    const spaceBelow = window.innerHeight - anchorRect.bottom - 8;
    const spaceAbove = anchorRect.top - 8;
    setFlipUp(popoverHeight > spaceBelow && spaceAbove > spaceBelow);
  }, [props.anchor, props.children]);

  // Close on outside click.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (props.anchor?.contains(target)) return;
      props.onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [props]);

  // Close on Escape.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") props.onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [props]);

  // Position relative to the anchor (fixed coords).
  const rect = props.anchor?.getBoundingClientRect();
  const minWidth = props.minWidth ?? 220;
  const popoverHeight = ref.current?.offsetHeight ?? 0;
  const style: React.CSSProperties = rect
    ? {
        // Position so the popover's top-right lines up with the anchor's
        // bottom-right (or bottom-right → anchor's top-right when flipped).
        // Clamp to viewport on the right edge.
        position: "fixed",
        top: flipUp ? Math.max(8, rect.top - popoverHeight - 4) : rect.bottom + 4,
        left: Math.max(8, Math.min(window.innerWidth - minWidth - 8, rect.right - minWidth)),
        minWidth
      }
    : { display: "none" };

  return (
    <div ref={ref} className="colFilterPopover" style={style} role="dialog" aria-label={props.title ?? "Filter"}>
      {props.title ? <div className="colFilterHeader">{props.title}</div> : null}
      {props.children}
      {!props.hideActions && (props.onClear ? (
        <div className="colFilterActions">
          <button type="button" className="btn" onClick={props.onClear}>Clear</button>
          <button type="button" className="btn btnPrimary" onClick={props.onClose}>Done</button>
        </div>
      ) : (
        <div className="colFilterActions">
          <button type="button" className="btn btnPrimary" onClick={props.onClose}>Done</button>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline funnel icon for the .thFilter button.
 * Small enough to tuck next to a column name without blowing up the header.
 */
export function IconFunnel({ size = 14, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M2 3h12l-4.5 5.5v4L7.5 14v-5.5L2 3z" />
    </svg>
  );
}
